import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { 
  ApiResponse, 
  LoginRequest, 
  RegisterRequest, 
  AuthResponse, 
  DashboardStats,
  User,
  Projet,
  Batiment,
  TypeMaison,
  ConfigFinancier,
  TypeMaisonBatimentConfigFinancier,
  Souscription,
  Echeancier,
  Paiement,
  Notification,
  Document as ApiDocument,
  ProjetFilters,
  EcheancierFilters,
  SouscriptionFilters,
  PersonneAContacter,
  ClientPersonneAContacter,
  DevisProjet,
  AssignationDevis,
  DemandeDevisRequest,
  TypeRelation,
  StatutMarital,
  RegimeMatrimonial,
  BatimentConfigurationPublic,
  Actualite
} from '../types/api';

class ApiService {
  private api: AxiosInstance;
  private refreshToken: string | null = null;

  constructor() {
    const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
    
    this.api = axios.create({
      baseURL: baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Intercepteur pour ajouter le token d'authentification
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Intercepteur pour gérer les erreurs et le refresh token
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await this.refreshAccessToken();
            const newToken = localStorage.getItem('access_token');
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            this.logout();
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Méthodes d'authentification
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/token/', credentials);
    const { access, refresh, user } = response.data;
    
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    localStorage.setItem('user', JSON.stringify(user));
    
    this.refreshToken = refresh;
    return response.data;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/register/', userData);
    const { access, refresh, user } = response.data;
    
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    localStorage.setItem('user', JSON.stringify(user));
    
    this.refreshToken = refresh;
    return response.data;
  }

  async refreshAccessToken(): Promise<void> {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) {
      throw new Error('No refresh token available');
    }

    const response: AxiosResponse<{ access: string }> = await this.api.post('/auth/token/refresh/', {
      refresh,
    });

    localStorage.setItem('access_token', response.data.access);
  }

  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    this.refreshToken = null;
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  // Méthodes pour les utilisateurs
  async getUsers(params?: any): Promise<ApiResponse<User>> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.get('/users/', { params });
    return response.data;
  }

  async getUser(id: number): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get(`/users/${id}/`);
    return response.data;
  }

  async updateUser(id: number, data: Partial<User>): Promise<User> {
    const response: AxiosResponse<User> = await this.api.put(`/users/${id}/`, data);
    return response.data;
  }

  async updateUserProfile(id: number, data: Partial<User>): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post(`/users/${id}/update-profile/`, data);
    
    // Mettre à jour le localStorage avec les nouvelles données utilisateur
    const updatedUser = response.data;
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    return updatedUser;
  }

  async changeUserPassword(id: number, passwordData: { current_password: string; new_password: string; confirm_password: string }): Promise<void> {
    await this.api.post(`/users/${id}/change-password/`, passwordData);
  }

  async activateUser(id: number): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post(`/users/${id}/activate/`);
    return response.data;
  }

  async deactivateUser(id: number): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post(`/users/${id}/deactivate/`);
    return response.data;
  }

  // Méthodes pour les projets
  async getProjets(params?: ProjetFilters): Promise<ApiResponse<Projet>> {
    const response: AxiosResponse<ApiResponse<Projet>> = await this.api.get('/projets/', { params });
    return response.data;
  }

  async getProjet(id: number): Promise<Projet> {
    const response: AxiosResponse<Projet> = await this.api.get(`/projets/${id}/`);
    return response.data;
  }

  async createProjet(data: Partial<Projet>): Promise<Projet> {
    const response: AxiosResponse<Projet> = await this.api.post('/projets/', data);
    return response.data;
  }

  async updateProjet(id: number, data: Partial<Projet>): Promise<Projet> {
    const response: AxiosResponse<Projet> = await this.api.put(`/projets/${id}/`, data);
    return response.data;
  }

  async deleteProjet(id: number): Promise<void> {
    await this.api.delete(`/projets/${id}/`);
  }

  async getProjetStatistiques(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/projets/${id}/statistiques/`);
    return response.data;
  }

  async getProjetMaisonsDisponibles(id: number): Promise<BatimentConfigurationPublic[]> {
    const response: AxiosResponse<BatimentConfigurationPublic[]> = await this.api.get(`/projets/${id}/maisons-disponibles/`);
    return response.data;
  }

  // Méthodes pour les bâtiments
  async getBatiments(params?: any): Promise<ApiResponse<Batiment>> {
    const response: AxiosResponse<ApiResponse<Batiment>> = await this.api.get('/batiments/', { params });
    return response.data;
  }

  async getBatiment(id: number): Promise<Batiment> {
    const response: AxiosResponse<Batiment> = await this.api.get(`/batiments/${id}/`);
    return response.data;
  }

  async createBatiment(data: Partial<Batiment>): Promise<Batiment> {
    const response: AxiosResponse<Batiment> = await this.api.post('/batiments/', data);
    return response.data;
  }

  async updateBatiment(id: number, data: Partial<Batiment>): Promise<Batiment> {
    const response: AxiosResponse<Batiment> = await this.api.put(`/batiments/${id}/`, data);
    return response.data;
  }

  async deleteBatiment(id: number): Promise<void> {
    await this.api.delete(`/batiments/${id}/`);
  }

  // Méthodes pour les types de maisons
  async getTypesMaison(params?: any): Promise<ApiResponse<TypeMaison>> {
    const response: AxiosResponse<ApiResponse<TypeMaison>> = await this.api.get('/types-maisons/', { params });
    return response.data;
  }

  async getTypeMaison(id: number): Promise<TypeMaison> {
    const response: AxiosResponse<TypeMaison> = await this.api.get(`/types-maisons/${id}/`);
    return response.data;
  }

  async createTypeMaison(data: Partial<TypeMaison>): Promise<TypeMaison> {
    const response: AxiosResponse<TypeMaison> = await this.api.post('/types-maisons/', data);
    return response.data;
  }

  async updateTypeMaison(id: number, data: Partial<TypeMaison>): Promise<TypeMaison> {
    const response: AxiosResponse<TypeMaison> = await this.api.put(`/types-maisons/${id}/`, data);
    return response.data;
  }

  async deleteTypeMaison(id: number): Promise<void> {
    await this.api.delete(`/types-maisons/${id}/`);
  }

  // Méthodes pour les souscriptions
  async getSouscriptions(params?: SouscriptionFilters): Promise<ApiResponse<Souscription>> {
    const response: AxiosResponse<ApiResponse<Souscription>> = await this.api.get('/souscriptions/', { params });
    return response.data;
  }

  async getSouscription(id: number): Promise<Souscription> {
    const response: AxiosResponse<Souscription> = await this.api.get(`/souscriptions/${id}/`);
    return response.data;
  }

  async createSouscription(data: Partial<Souscription>): Promise<Souscription> {
    const response: AxiosResponse<Souscription> = await this.api.post('/souscriptions/', data);
    return response.data;
  }

  async updateSouscription(id: number, data: Partial<Souscription>): Promise<Souscription> {
    const response: AxiosResponse<Souscription> = await this.api.put(`/souscriptions/${id}/`, data);
    return response.data;
  }

  async deleteSouscription(id: number): Promise<void> {
    await this.api.delete(`/souscriptions/${id}/`);
  }

  async genererEcheancier(id: number): Promise<Souscription> {
    const response: AxiosResponse<Souscription> = await this.api.post(`/souscriptions/${id}/generer-echeancier/`);
    return response.data;
  }

  // Méthodes pour les échéanciers
  async getEcheanciers(params?: EcheancierFilters): Promise<ApiResponse<Echeancier> | Echeancier[]> {
    const response = await this.api.get('/echeanciers/', { params });
    
    // Si l'API retourne directement un tableau (sans pagination), on le retourne tel quel
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    // Sinon, on retourne l'objet paginé
    return response.data;
  }

  async getEcheancier(id: number): Promise<Echeancier> {
    const response: AxiosResponse<Echeancier> = await this.api.get(`/echeanciers/${id}/`);
    return response.data;
  }

  async updateEcheancier(id: number, data: Partial<Echeancier>): Promise<Echeancier> {
    const response: AxiosResponse<Echeancier> = await this.api.put(`/echeanciers/${id}/`, data);
    return response.data;
  }

  // Méthodes pour les paiements
  async getPaiements(params?: any): Promise<ApiResponse<Paiement>> {
    const response: AxiosResponse<ApiResponse<Paiement>> = await this.api.get('/paiements/', { params });
    return response.data;
  }

  async getPaiement(id: number): Promise<Paiement> {
    const response: AxiosResponse<Paiement> = await this.api.get(`/paiements/${id}/`);
    return response.data;
  }

  async createPaiement(data: Partial<Paiement>): Promise<Paiement> {
    const response: AxiosResponse<Paiement> = await this.api.post('/paiements/', data);
    return response.data;
  }

  async createMultiSubscriptionPaiement(data: {
    montant_total_paye: number;
    date_paiement: string;
    mode_paiement: string;
    reference_paiement?: string;
    commentaire?: string;
    repartitions: Array<{
      souscription_id: number;
      montant_alloue: number;
    }>;
  }): Promise<Paiement> {
    const response: AxiosResponse<Paiement> = await this.api.post('/paiements/', data);
    return response.data;
  }

  async updatePaiement(id: number, data: Partial<Paiement>): Promise<Paiement> {
    const response: AxiosResponse<Paiement> = await this.api.put(`/paiements/${id}/`, data);
    return response.data;
  }

  async validerPaiement(id: number): Promise<Paiement> {
    const response: AxiosResponse<Paiement> = await this.api.post(`/paiements/${id}/valider/`);
    return response.data;
  }

  async rejeterPaiement(id: number): Promise<Paiement> {
    const response: AxiosResponse<Paiement> = await this.api.post(`/paiements/${id}/rejeter/`);
    return response.data;
  }

  async telechargerFacture(id: number): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.api.get(`/paiements/${id}/telecharger_facture/`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Méthodes pour les notifications
  async getNotifications(params?: any): Promise<ApiResponse<Notification>> {
    const response: AxiosResponse<ApiResponse<Notification>> = await this.api.get('/notifications/', { params });
    return response.data;
  }

  async getNotification(id: number): Promise<Notification> {
    const response: AxiosResponse<Notification> = await this.api.get(`/notifications/${id}/`);
    return response.data;
  }

  async marquerNotificationLu(id: number): Promise<Notification> {
    const response: AxiosResponse<Notification> = await this.api.post(`/notifications/${id}/marquer-lu/`);
    return response.data;
  }

  async marquerToutesNotificationsLues(): Promise<void> {
    await this.api.post('/notifications/marquer-toutes-lues/');
  }

  async getNotificationsNonLues(): Promise<ApiResponse<Notification>> {
    const response: AxiosResponse<ApiResponse<Notification>> = await this.api.get('/notifications/non-lues/');
    return response.data;
  }

  // Méthodes pour les statistiques du tableau de bord
  async getDashboardStats(): Promise<DashboardStats> {
    const response: AxiosResponse<DashboardStats> = await this.api.get('/admin/api/stats/');
    return response.data;
  }

  // Méthodes pour les rapports
  async getRapportPaiements(params?: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/rapports/paiements/', { params });
    return response.data;
  }

  async getRapportProjets(params?: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/rapports/projets/', { params });
    return response.data;
  }

  // Méthodes pour les documents
  async getDocuments(params?: any): Promise<ApiResponse<ApiDocument>> {
    const response: AxiosResponse<ApiResponse<ApiDocument>> = await this.api.get('/documents/', { params });
    return response.data;
  }

  async getDocument(id: number): Promise<ApiDocument> {
    const response: AxiosResponse<ApiDocument> = await this.api.get(`/documents/${id}/`);
    return response.data;
  }

  // Méthodes pour les actualités
  async getActualites(params?: any): Promise<ApiResponse<Actualite>> {
    const response: AxiosResponse<ApiResponse<Actualite>> = await this.api.get('/actualites/', { params });
    return response.data;
  }

  async getActualite(id: number): Promise<Actualite> {
    const response: AxiosResponse<Actualite> = await this.api.get(`/actualites/${id}/`);
    return response.data;
  }

  async createActualite(data: Partial<Actualite>): Promise<Actualite> {
    const response: AxiosResponse<Actualite> = await this.api.post('/actualites/', data);
    return response.data;
  }

  async updateActualite(id: number, data: Partial<Actualite>): Promise<Actualite> {
    const response: AxiosResponse<Actualite> = await this.api.put(`/actualites/${id}/`, data);
    return response.data;
  }

  async deleteActualite(id: number): Promise<void> {
    await this.api.delete(`/actualites/${id}/`);
  }

  // Méthodes pour les configurations financières
  async getConfigsFinancieres(params?: any): Promise<ApiResponse<ConfigFinancier>> {
    const response: AxiosResponse<ApiResponse<ConfigFinancier>> = await this.api.get('/configs-financieres/', { params });
    return response.data;
  }

  async getConfigFinanciere(id: number): Promise<ConfigFinancier> {
    const response: AxiosResponse<ConfigFinancier> = await this.api.get(`/configs-financieres/${id}/`);
    return response.data;
  }

  async createConfigFinanciere(data: Partial<ConfigFinancier>): Promise<ConfigFinancier> {
    const response: AxiosResponse<ConfigFinancier> = await this.api.post('/configs-financieres/', data);
    return response.data;
  }

  async updateConfigFinanciere(id: number, data: Partial<ConfigFinancier>): Promise<ConfigFinancier> {
    const response: AxiosResponse<ConfigFinancier> = await this.api.put(`/configs-financieres/${id}/`, data);
    return response.data;
  }

  async deleteConfigFinanciere(id: number): Promise<void> {
    await this.api.delete(`/configs-financieres/${id}/`);
  }

  // Méthodes pour les associations type maison - config financière
  async getConfigsFinancieresAssociations(params?: any): Promise<ApiResponse<TypeMaisonBatimentConfigFinancier>> {
    const response: AxiosResponse<ApiResponse<TypeMaisonBatimentConfigFinancier>> = await this.api.get('/configs-financieres-associations/', { params });
    return response.data;
  }

  async getConfigsFinancieresByTypeMaisonBatiment(typeMaisonBatimentId: number): Promise<ApiResponse<TypeMaisonBatimentConfigFinancier>> {
    const response: AxiosResponse<ApiResponse<TypeMaisonBatimentConfigFinancier>> = await this.api.get(`/configs-financieres-associations/by-type-maison-batiment/?type_maison_batiment=${typeMaisonBatimentId}`);
    return response.data;
  }

  // Méthodes pour les personnes à contacter
  async getPersonnesAContacter(params?: any): Promise<ApiResponse<PersonneAContacter>> {
    const response: AxiosResponse<ApiResponse<PersonneAContacter>> = await this.api.get('/personnes-a-contacter/', { params });
    return response.data;
  }

  async getPersonneAContacter(id: number): Promise<PersonneAContacter> {
    const response: AxiosResponse<PersonneAContacter> = await this.api.get(`/personnes-a-contacter/${id}/`);
    return response.data;
  }

  async createPersonneAContacter(data: Partial<PersonneAContacter>): Promise<PersonneAContacter> {
    const response: AxiosResponse<PersonneAContacter> = await this.api.post('/personnes-a-contacter/', data);
    return response.data;
  }

  async updatePersonneAContacter(id: number, data: Partial<PersonneAContacter>): Promise<PersonneAContacter> {
    const response: AxiosResponse<PersonneAContacter> = await this.api.put(`/personnes-a-contacter/${id}/`, data);
    return response.data;
  }

  async deletePersonneAContacter(id: number): Promise<void> {
    await this.api.delete(`/personnes-a-contacter/${id}/`);
  }

  // Méthodes pour les relations client-personne à contacter
  async getClientPersonnesAContacter(params?: any): Promise<ApiResponse<ClientPersonneAContacter>> {
    const response: AxiosResponse<ApiResponse<ClientPersonneAContacter>> = await this.api.get('/client-personnes-a-contacter/', { params });
    return response.data;
  }

  async getClientPersonneAContacter(id: number): Promise<ClientPersonneAContacter> {
    const response: AxiosResponse<ClientPersonneAContacter> = await this.api.get(`/client-personnes-a-contacter/${id}/`);
    return response.data;
  }

  async createClientPersonneAContacter(data: Partial<ClientPersonneAContacter>): Promise<ClientPersonneAContacter> {
    const response: AxiosResponse<ClientPersonneAContacter> = await this.api.post('/client-personnes-a-contacter/', data);
    return response.data;
  }

  async updateClientPersonneAContacter(id: number, data: Partial<ClientPersonneAContacter>): Promise<ClientPersonneAContacter> {
    const response: AxiosResponse<ClientPersonneAContacter> = await this.api.put(`/client-personnes-a-contacter/${id}/`, data);
    return response.data;
  }

  async deleteClientPersonneAContacter(id: number): Promise<void> {
    await this.api.delete(`/client-personnes-a-contacter/${id}/`);
  }

  // Méthodes pour les devis de projet
  async getDevisProjets(params?: any): Promise<ApiResponse<DevisProjet>> {
    const response: AxiosResponse<ApiResponse<DevisProjet>> = await this.api.get('/devis-projets/', { params });
    return response.data;
  }

  async getDevisProjet(id: number): Promise<DevisProjet> {
    const response: AxiosResponse<DevisProjet> = await this.api.get(`/devis-projets/${id}/`);
    return response.data;
  }

  // Méthodes pour les assignations de devis
  async getAssignationsDevis(params?: any): Promise<ApiResponse<AssignationDevis>> {
    const response: AxiosResponse<ApiResponse<AssignationDevis>> = await this.api.get('/assignations-devis/', { params });
    return response.data;
  }

  async getAssignationDevis(id: number): Promise<AssignationDevis> {
    const response: AxiosResponse<AssignationDevis> = await this.api.get(`/assignations-devis/${id}/`);
    return response.data;
  }

  async accepterAssignationDevis(id: number): Promise<void> {
    await this.api.post(`/assignations-devis/${id}/accepter/`);
  }

  async refuserAssignationDevis(id: number): Promise<void> {
    await this.api.post(`/assignations-devis/${id}/refuser/`);
  }

  // Méthodes pour les demandes de devis
  async faireDemandeDevis(data: DemandeDevisRequest): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/demandes-devis/faire-demande/', data);
    return response.data;
  }

  async getMesDemandesDevis(): Promise<AssignationDevis[]> {
    const response: AxiosResponse<AssignationDevis[]> = await this.api.get('/demandes-devis/mes-demandes/');
    return response.data;
  }

  // Méthodes pour les types de relations
  async getTypesRelation(params?: any): Promise<ApiResponse<TypeRelation>> {
    const response: AxiosResponse<ApiResponse<TypeRelation>> = await this.api.get('/types-relations/', { params });
    return response.data;
  }

  // Méthodes pour les statuts maritaux
  async getStatutsMaritaux(params?: any): Promise<ApiResponse<StatutMarital>> {
    const response: AxiosResponse<ApiResponse<StatutMarital>> = await this.api.get('/statuts-maritaux/', { params });
    return response.data;
  }

  // Méthodes pour les régimes matrimoniaux
  async getRegimesMatrimoniaux(params?: any): Promise<ApiResponse<RegimeMatrimonial>> {
    const response: AxiosResponse<ApiResponse<RegimeMatrimonial>> = await this.api.get('/regimes-matrimoniaux/', { params });
    return response.data;
  }

  async getTypesMaisonsDisponibles(): Promise<BatimentConfigurationPublic[]> {
    const response = await this.api.get('/demandes-devis/types-maisons-disponibles/');
    return response.data;
  }
}

// Export d'une instance singleton
export const apiService = new ApiService();
export default apiService;
